import { useEffect, useState } from "react";
import { api, euro, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Package, Pencil, Undo2, Search } from "lucide-react";
import { toast } from "sonner";

const empty = {
  center_id: "",
  type: "audifono",
  brand: "",
  model: "",
  description: "",
  quantity: 0,
  min_quantity: 0,
  unit_price: 0,
  cost_price: "",
  pila_size: "",
  supplier_id: "",
  warranty_years: 2,
  serial_number: "",
  serials_text: "",  // texto multilínea / coma-separado para edición
  is_trial: false,
  catalog_key: "",   // clave "brand||model" cuando se rellenó desde catálogo
};

const TYPE_OPTIONS = [
  { value: "audifono", label: "Audífono" },
  { value: "accesorio", label: "Accesorio auditivo" },
  { value: "pila", label: "Pila" },
  { value: "tapon_bano_medida", label: "Tapón baño · a medida" },
  { value: "tapon_bano_standard", label: "Tapón baño · standard" },
  { value: "tapon_ruido_medida", label: "Tapón ruido · a medida" },
  { value: "tapon_ruido_standard", label: "Tapón ruido · standard" },
  { value: "otro", label: "Otro" },
];

const TYPE_LABEL = Object.fromEntries(TYPE_OPTIONS.map((o) => [o.value, o.label]));
const PILA_SIZES = ["675", "13", "312", "10"];

export default function Stock() {
  const { user, centers } = useAuth();
  const isAdmin = ["admin", "center_admin"].includes(user?.role);
  const canReturn = ["admin", "center_admin"].includes(user?.role) || user?.role === "gerente";
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [patients, setPatients] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignData, setAssignData] = useState({ patient_id: "", trial_days: 15, notes: "" });
  const [returnTarget, setReturnTarget] = useState(null);
  const [returnData, setReturnData] = useState({ quantity: 1, serial: "", reason: "", refund_amount: 0, notes: "" });
  // Buscador y filtro por centro (ubicación del artículo)
  const [search, setSearch] = useState("");
  const [centerFilter, setCenterFilter] = useState("all");

  const centerName = (id) => centers.find((c) => c.id === id)?.name || "—";
  const norm = (s) => (s || "").toString().toLowerCase();
  const filteredItems = items.filter((it) => {
    if (centerFilter !== "all" && it.center_id !== centerFilter) return false;
    if (!search.trim()) return true;
    const q = norm(search);
    const sup = suppliers.find((s) => s.id === it.supplier_id)?.name;
    return [it.brand, it.model, it.description, it.serial_number, TYPE_LABEL[it.type], sup, ...(it.serials || [])]
      .some((v) => norm(v).includes(q));
  });

  const load = async () => {
    const [{ data: stock }, { data: sup }, { data: pats }, { data: sett }] = await Promise.all([
      api.get("/stock"),
      api.get("/suppliers"),
      api.get("/patients"),
      api.get("/settings"),
    ]);
    setItems(stock);
    setSuppliers(sup);
    setPatients(pats);
    setCatalog(sett?.hearing_aid_catalog || []);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const serials = (form.serials_text || "")
        .split(/[\n,;]/)
        .map((s) => s.trim())
        .filter(Boolean);
      const payload = {
        ...form,
        center_id: form.center_id || user?.center_id || centers[0]?.id,
        quantity: parseInt(form.quantity) || 0,
        min_quantity: parseInt(form.min_quantity) || 0,
        unit_price: parseFloat(form.unit_price) || 0,
        cost_price: form.cost_price === "" || form.cost_price == null || isNaN(parseFloat(form.cost_price)) ? null : parseFloat(form.cost_price),
        warranty_years: parseFloat(form.warranty_years) || 0,
        warranty_months: Math.round((parseFloat(form.warranty_years) || 0) * 12),
        pila_size: form.type === "pila" ? form.pila_size : "",
        is_trial: !!form.is_trial,
        serial_number: form.serial_number || "",
        serials,
        supplier_id: form.supplier_id || null,
        catalog_key: form.catalog_key || null,
      };
      delete payload.serials_text;
      if (editingId) {
        await api.patch(`/stock/${editingId}`, payload);
        toast.success("Producto actualizado");
      } else {
        await api.post("/stock", payload);
        toast.success("Producto añadido");
      }
      setOpen(false);
      setEditingId(null);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // Al elegir un producto del catálogo, autocompleta marca/modelo/precio/garantía/proveedor
  const applyCatalogItem = (catalogKey) => {
    if (!catalogKey) return;
    const item = catalog.find((c) => `${c.brand}||${c.model}` === catalogKey);
    if (!item) return;
    setForm((f) => ({
      ...f,
      catalog_key: catalogKey,
      type: item.type || f.type,
      brand: item.brand || "",
      model: item.model || "",
      unit_price: item.price ?? 0,
      description: item.notes || f.description,
      warranty_years: item.warranty_years || f.warranty_years,
      supplier_id: item.default_supplier_id || f.supplier_id,
      pila_size: item.type === "pila" ? (item.pila_size || f.pila_size) : f.pila_size,
    }));
    toast.success(`Autocompletado: ${item.brand} ${item.model}`);
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setForm({
      center_id: it.center_id || "",
      type: it.type || "audifono",
      brand: it.brand || "",
      model: it.model || "",
      description: it.description || "",
      quantity: it.quantity ?? 0,
      min_quantity: it.min_quantity ?? 0,
      unit_price: it.unit_price ?? 0,
      cost_price: it.cost_price ?? "",
      pila_size: it.pila_size || "",
      supplier_id: it.supplier_id || "",
      warranty_years: it.warranty_years || (it.warranty_months ? Number((it.warranty_months / 12).toFixed(2)) : 0),
      serial_number: it.serial_number || "",
      serials_text: (it.serials?.length ? it.serials : (it.serial_number ? [it.serial_number] : [])).join("\n"),
      is_trial: !!it.is_trial,
      catalog_key: it.catalog_key || "",
    });
    setOpen(true);
  };

  const openNew = () => {
    setEditingId(null);
    setForm(empty);
    setOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar artículo de stock?")) return;
    await api.delete(`/stock/${id}`);
    toast.success("Eliminado");
    load();
  };

  const updateQty = async (it, delta) => {
    const newQ = Math.max(0, (it.quantity || 0) + delta);
    await api.patch(`/stock/${it.id}`, { ...it, quantity: newQ });
    load();
  };

  return (
    <div className="space-y-6" data-testid="stock-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#6E7772] font-semibold">
            Inventario
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#2A2F2C] mt-1">
            Stock general
          </h1>
          <p className="text-sm text-[#6E7772] mt-1">Inventario compartido entre los dos centros.</p>
        </div>

        <div className="flex gap-3">
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(empty); } }}>
            {isAdmin && (
              <DialogTrigger asChild>
                <Button onClick={openNew} data-testid="new-stock-btn" className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white">
                  <Plus className="h-4 w-4 mr-2" /> Nuevo artículo
                </Button>
              </DialogTrigger>
            )}
            <DialogContent className="max-w-2xl bg-white">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">{editingId ? "Editar artículo" : "Nuevo artículo"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {catalog.length > 0 && !editingId && (
                  <div className="md:col-span-2 p-3 bg-[#F7F6F3] border border-[#E5E3DB] rounded-md">
                    <Label className="flex items-center gap-1.5 text-sm font-medium text-[#4A5D4E]">
                      <Search className="h-3.5 w-3.5" strokeWidth={2} /> Buscar en catálogo (autocompleta marca, modelo, precio, garantía y proveedor)
                    </Label>
                    <Select
                      value={form.catalog_key || "none"}
                      onValueChange={(v) => {
                        if (v === "none") {
                          setForm((f) => ({ ...f, catalog_key: "" }));
                        } else {
                          applyCatalogItem(v);
                        }
                      }}
                    >
                      <SelectTrigger data-testid="stock-catalog-select" className="bg-white mt-1">
                        <SelectValue placeholder="— Seleccionar del catálogo —" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[400px]">
                        <SelectItem value="none">— Rellenar a mano —</SelectItem>
                        {catalog.map((c, i) => (
                          <SelectItem key={i} value={`${c.brand}||${c.model}`}>
                            {c.brand} · {c.model} · {euro(c.price || 0)}
                            {c.warranty_years ? ` · ${c.warranty_years} años garantía` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-[#6E7772] mt-1">
                      Tras seleccionar, solo tienes que introducir el nº de serie (audífonos) o la cantidad.
                    </p>
                  </div>
                )}
                <div>
                  <Label>Tipo</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="stock-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TYPE_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Marca</Label>
                  <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
                </div>
                <div>
                  <Label>Modelo</Label>
                  <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
                </div>
                <div className="md:col-span-2">
                  <Label>{form.type === "accesorio" ? "Descripción del producto (información a rellenar)" : "Descripción"}</Label>
                  <Input
                    data-testid="stock-description"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder={form.type === "accesorio" ? "Detalla el producto vendido (tipo, color, características)" : ""}
                  />
                </div>
                {form.type === "pila" && (
                  <div className="md:col-span-2">
                    <Label>Tamaño de pila *</Label>
                    <Select
                      value={form.pila_size}
                      onValueChange={(v) => setForm({ ...form, pila_size: v })}
                    >
                      <SelectTrigger data-testid="stock-pila-size">
                        <SelectValue placeholder="Selecciona el tamaño" />
                      </SelectTrigger>
                      <SelectContent>
                        {PILA_SIZES.map((s) => (
                          <SelectItem key={s} value={s}>
                            Pila {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {form.type === "audifono" && (
                  <>
                    <div>
                      <Label>Garantía (años)</Label>
                      <Input
                        data-testid="stock-warranty"
                        type="number"
                        min={0}
                        step="0.5"
                        value={form.warranty_years}
                        onChange={(e) => setForm({ ...form, warranty_years: e.target.value })}
                        placeholder="Ej. 2"
                      />
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-sm mt-6">
                        <Checkbox
                          checked={!!form.is_trial}
                          onCheckedChange={(v) => setForm({ ...form, is_trial: !!v })}
                          data-testid="stock-is-trial"
                        />
                        Audífono de TRIAL (para pruebas con pacientes)
                      </label>
                    </div>
                  </>
                )}
                <div className="md:col-span-2">
                  <Label>Números de serie {form.type === "audifono" ? "(uno por línea, o separados por coma/;)" : "(opcional — uno por línea, o separados por coma/;)"}</Label>
                  <textarea
                    data-testid="stock-serials"
                    value={form.serials_text}
                    onChange={(e) => {
                      const txt = e.target.value;
                      const arr = txt.split(/[\n,;]/).map((s) => s.trim()).filter(Boolean);
                      setForm((f) => ({
                        ...f,
                        serials_text: txt,
                        serial_number: arr[0] || "",
                        quantity: f.type === "audifono"
                          ? arr.length
                          : Math.max(parseInt(f.quantity) || 0, arr.length),
                      }));
                    }}
                    placeholder={"PA001\nPA002\nPA003"}
                    rows={4}
                    className="w-full px-3 py-2 text-sm border border-[#E5E3DB] rounded-md bg-white font-mono"
                  />
                  <p className="text-xs text-[#6E7772] mt-1">
                    {form.type === "audifono"
                      ? "Cada unidad tiene su propio número de serie. La cantidad se calcula automáticamente."
                      : "Puedes registrar un número de serie por unidad. Si introduces más seriales que la cantidad, la cantidad se ajusta automáticamente."}
                  </p>
                </div>
                <div>
                  <Label>Cantidad {form.type === "audifono" && <span className="text-[10px] text-[#6E7772]">(auto)</span>}</Label>
                  <Input
                    data-testid="stock-quantity"
                    type="number"
                    value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                    disabled={form.type === "audifono"}
                  />
                </div>
                <div>
                  <Label>Stock mínimo</Label>
                  <Input
                    type="number"
                    value={form.min_quantity}
                    onChange={(e) => setForm({ ...form, min_quantity: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Precio venta (IVA incluido) €</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.unit_price}
                    onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                  />
                  <p className="text-[10px] text-[#6E7772] mt-0.5">Es el precio final que paga el cliente.</p>
                </div>
                <div>
                  <Label>Coste de compra (proveedor) €</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.cost_price}
                    onChange={(e) => setForm({ ...form, cost_price: e.target.value })}
                    data-testid="stock-cost-price"
                  />
                  <p className="text-[10px] text-[#6E7772] mt-0.5">Lo que te cobra el proveedor. Se usa para calcular el margen en Informes.</p>
                </div>
                <div>
                  <Label>Ubicación (centro)</Label>
                  <Select
                    value={form.center_id || "none"}
                    onValueChange={(v) => setForm({ ...form, center_id: v === "none" ? "" : v })}
                  >
                    <SelectTrigger data-testid="stock-center-select"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin especificar</SelectItem>
                      {centers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Proveedor</Label>
                  <Select
                    value={form.supplier_id || "none"}
                    onValueChange={(v) => setForm({ ...form, supplier_id: v === "none" ? "" : v })}
                  >
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin proveedor</SelectItem>
                      {suppliers.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter className="md:col-span-2">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    data-testid="save-stock-btn"
                    className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
                  >
                    {saving ? "Guardando…" : "Guardar"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6E7772]" />
          <Input
            className="pl-9 bg-white"
            placeholder="Buscar por marca, modelo, nº de serie, descripción o proveedor…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="stock-search-input"
          />
        </div>
        <div className="w-56">
          <Select value={centerFilter} onValueChange={setCenterFilter}>
            <SelectTrigger className="bg-white" data-testid="stock-center-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las ubicaciones</SelectItem>
              {centers.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {(search || centerFilter !== "all") && (
          <span className="text-xs text-[#6E7772]">{filteredItems.length} artículo(s)</span>
        )}
      </div>

      <div className="rounded-lg border border-[#E5E3DB] bg-white">
        <Table>
          <TableHeader>
            <TableRow className="border-[#E5E3DB]">
              <TableHead>Tipo</TableHead>
              <TableHead>Producto</TableHead>
              <TableHead>Ubicación</TableHead>
              <TableHead className="text-center">Stock</TableHead>
              <TableHead className="text-center">Garantía</TableHead>
              <TableHead className="text-right">Precio (IVA incl.)</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-[#6E7772] py-12">
                  <Package strokeWidth={1.5} className="h-8 w-8 mx-auto mb-2 opacity-60" />
                  {items.length === 0 ? "Sin productos en stock." : "Ningún artículo coincide con la búsqueda."}
                </TableCell>
              </TableRow>
            )}
            {filteredItems.map((it) => {
              const low = (it.quantity || 0) <= (it.min_quantity || 0);
              const supplier = suppliers.find((s) => s.id === it.supplier_id);
              return (
                <TableRow key={it.id} className="border-[#E5E3DB]" data-testid={`stock-row-${it.id}`}>
                  <TableCell>
                    <Badge variant="outline" className="border-[#E5E3DB] font-normal">
                      {TYPE_LABEL[it.type] || it.type}
                    </Badge>
                    {it.type === "pila" && it.pila_size && (
                      <Badge className="ml-1 bg-[#EAE6DF] text-[#2A2F2C]">{it.pila_size}</Badge>
                    )}
                    {it.is_trial && (
                      <Badge className="ml-1 bg-[#C07F67] text-white">TRIAL</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">
                      {it.brand || "—"} {it.model}
                    </div>
                    <div className="text-xs text-[#6E7772] space-y-0.5">
                      {it.serial_number && !it.serials?.length && <div>S/N: {it.serial_number}</div>}
                      {it.serials?.length > 0 && (
                        <div className="text-xs text-[#6E7772]">
                          S/N: {it.serials.slice(0, 3).join(", ")}{it.serials.length > 3 ? ` … +${it.serials.length - 3}` : ""}
                        </div>
                      )}
                      {it.description && <div>{it.description}</div>}
                      {supplier && <div>Proveedor: {supplier.name}</div>}
                      {it.warranty_months > 0 && (
                        <div>Garantía: {it.warranty_months} meses</div>
                      )}
                      {it.trial_patient_id && (
                        <div className="text-[#C07F67] font-medium">
                          En uso por paciente · trial activo
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="border-[#C9D3CB] text-[#4A5D4E] font-normal" data-testid={`stock-location-${it.id}`}>
                      {centerName(it.center_id)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-center gap-2">
                      {isAdmin && (
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => updateQty(it, -1)}>
                          −
                        </Button>
                      )}
                      <Badge
                        className={
                          low
                            ? "bg-[#E8D1D1] text-[#2A2F2C]"
                            : "bg-[#D1E0D7] text-[#2A2F2C]"
                        }
                      >
                        {it.quantity}
                      </Badge>
                      {isAdmin && (
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => updateQty(it, 1)}>
                          +
                        </Button>
                      )}
                    </div>
                    {low && <div className="text-[10px] text-[#C07F67] text-center mt-1">Stock bajo</div>}
                  </TableCell>
                  <TableCell className="text-center">
                    {it.warranty_months > 0 ? (
                      <Badge className="bg-[#D1E0D7] text-[#2A2F2C]">
                        {(it.warranty_years || it.warranty_months / 12).toLocaleString("es-ES", { maximumFractionDigits: 1 })} años
                      </Badge>
                    ) : (
                      <span className="text-xs text-[#6E7772]">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">{euro(it.unit_price)}</TableCell>
                  <TableCell className="space-x-1">
                    {it.is_trial && !it.trial_patient_id && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAssignTarget(it)}
                        data-testid={`assign-trial-${it.id}`}
                        className="h-7 text-[11px] border-[#C07F67] text-[#C07F67] hover:bg-[#FDF3EE]"
                      >
                        Asignar trial
                      </Button>
                    )}
                    {it.trial_patient_id && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          try {
                            await api.post(`/stock/${it.id}/return-trial`);
                            toast.success("Trial devuelto");
                            load();
                          } catch (err) {
                            toast.error(formatApiError(err));
                          }
                        }}
                        data-testid={`return-trial-stock-${it.id}`}
                        className="h-7 text-[11px]"
                      >
                        Devolver trial
                      </Button>
                    )}
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => startEdit(it)}
                        data-testid={`edit-stock-${it.id}`}
                        title="Editar"
                      >
                        <Pencil className="h-4 w-4 text-[#4A5D4E]" />
                      </Button>
                    )}
                    {canReturn && (it.quantity || 0) > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setReturnTarget(it);
                          setReturnData({
                            quantity: 1,
                            serial: (it.serials && it.serials[0]) || "",
                            reason: "",
                            refund_amount: it.unit_price || 0,
                            notes: "",
                          });
                        }}
                        data-testid={`return-supplier-${it.id}`}
                        title="Devolver al proveedor"
                      >
                        <Undo2 className="h-4 w-4 text-[#C07F67]" />
                      </Button>
                    )}
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => remove(it.id)}
                        data-testid={`delete-stock-${it.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-[#C07F67]" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Trial assignment dialog */}
      <Dialog open={!!assignTarget} onOpenChange={(v) => !v && setAssignTarget(null)}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              Asignar trial · {assignTarget?.brand} {assignTarget?.model}
            </DialogTitle>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!assignData.patient_id) {
                toast.error("Selecciona un paciente");
                return;
              }
              try {
                await api.post(`/stock/${assignTarget.id}/assign-trial`, {
                  patient_id: assignData.patient_id,
                  trial_days: parseInt(assignData.trial_days) || 15,
                  notes: assignData.notes || "",
                });
                toast.success("Trial asignado");
                setAssignTarget(null);
                setAssignData({ patient_id: "", trial_days: 15, notes: "" });
                load();
              } catch (err) {
                toast.error(formatApiError(err));
              }
            }}
          >
            <div>
              <Label>Paciente *</Label>
              <Select
                value={assignData.patient_id}
                onValueChange={(v) => setAssignData({ ...assignData, patient_id: v })}
              >
                <SelectTrigger data-testid="trial-patient-select">
                  <SelectValue placeholder="Selecciona paciente" />
                </SelectTrigger>
                <SelectContent>
                  {patients.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      #{p.patient_number} · {p.name} {p.surname || ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Duración (días)</Label>
              <Input
                type="number"
                min={1}
                value={assignData.trial_days}
                onChange={(e) => setAssignData({ ...assignData, trial_days: e.target.value })}
              />
            </div>
            <div>
              <Label>Notas</Label>
              <Input
                value={assignData.notes}
                onChange={(e) => setAssignData({ ...assignData, notes: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAssignTarget(null)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                data-testid="confirm-trial-btn"
                className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
              >
                Asignar trial
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Diálogo Devolución al proveedor */}
      <Dialog open={!!returnTarget} onOpenChange={(v) => !v && setReturnTarget(null)}>
        <DialogContent className="max-w-lg bg-white" data-testid="return-supplier-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <Undo2 className="h-5 w-5 text-[#C07F67]" strokeWidth={2} />
              Devolver al proveedor
            </DialogTitle>
          </DialogHeader>
          {returnTarget && (
            <form
              className="space-y-4"
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  const hasSerials = (returnTarget.serials || []).length > 0;
                  const payload = {
                    source_type: "supplier",
                    quantity: hasSerials ? 1 : (parseInt(returnData.quantity) || 1),
                    serial: hasSerials ? (returnData.serial || null) : null,
                    reason: returnData.reason || "",
                    restock_status: "devuelto_proveedor",
                    notes: returnData.notes || "",
                    refund_amount: parseFloat(returnData.refund_amount) || 0,
                  };
                  await api.post(`/stock/${returnTarget.id}/return-to-supplier`, payload);
                  toast.success("Devolución registrada. Stock actualizado.");
                  setReturnTarget(null);
                  load();
                } catch (err) {
                  toast.error(formatApiError(err));
                }
              }}
            >
              <div className="p-3 bg-[#F7F6F3] border border-[#E5E3DB] rounded text-sm">
                <div className="font-medium text-[#2A2F2C]">
                  {returnTarget.brand || "—"} {returnTarget.model || ""}
                </div>
                <div className="text-xs text-[#6E7772] mt-1">
                  Stock actual: {returnTarget.quantity} unidad(es)
                  {suppliers.find((s) => s.id === returnTarget.supplier_id) &&
                    ` · Proveedor: ${suppliers.find((s) => s.id === returnTarget.supplier_id).name}`}
                </div>
              </div>

              {returnTarget.serials?.length > 0 ? (
                <div>
                  <Label>Nº de serie a devolver *</Label>
                  <Select
                    value={returnData.serial}
                    onValueChange={(v) => setReturnData({ ...returnData, serial: v })}
                  >
                    <SelectTrigger data-testid="return-serial-select">
                      <SelectValue placeholder="Selecciona el serial" />
                    </SelectTrigger>
                    <SelectContent>
                      {(returnTarget.serials || []).map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <Label>Unidades a devolver *</Label>
                  <Input
                    type="number"
                    min={1}
                    max={returnTarget.quantity || 1}
                    value={returnData.quantity}
                    onChange={(e) => setReturnData({ ...returnData, quantity: e.target.value })}
                    data-testid="return-quantity"
                  />
                </div>
              )}

              <div>
                <Label>Motivo *</Label>
                <Select
                  value={returnData.reason || undefined}
                  onValueChange={(v) => setReturnData({ ...returnData, reason: v })}
                >
                  <SelectTrigger data-testid="return-reason-select">
                    <SelectValue placeholder="Selecciona motivo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="defectuoso">Defectuoso / no funciona</SelectItem>
                    <SelectItem value="error_pedido">Error de pedido</SelectItem>
                    <SelectItem value="sobre_stock">Sobre-stock</SelectItem>
                    <SelectItem value="cambio_modelo">Cambio de modelo / actualización</SelectItem>
                    <SelectItem value="otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Importe reembolsado (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  value={returnData.refund_amount}
                  onChange={(e) => setReturnData({ ...returnData, refund_amount: e.target.value })}
                  data-testid="return-refund-amount"
                />
              </div>

              <div>
                <Label>Notas</Label>
                <Input
                  value={returnData.notes}
                  onChange={(e) => setReturnData({ ...returnData, notes: e.target.value })}
                  data-testid="return-notes"
                  placeholder="Nº albarán de devolución, contacto, etc."
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setReturnTarget(null)}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  data-testid="confirm-return-btn"
                  className="bg-[#C07F67] hover:bg-[#B06E56] text-white"
                >
                  Registrar devolución
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

